import { useState } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { copyToClipboard } from "@/lib/utils";
import { Copy, Trash2 } from "lucide-react";
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function CharacterCounter() {
  const [text, setText] = useState("");
  return (
    <ToolPageLayout
      title="Character Counter"
      description="Count characters, letters, and numbers in your text instantly with our free online tool. Perfect for social media, SEO, and academic writing."
      h1="Character Counter"
      category="Text & Word"
      categoryPath="/text-word"
      howToUse={[
        "Paste your text into the input area.",
        "View the real-time character count, character count without spaces, letter count, and number count."
      ]}
      faq={[
        { question: "What does this character counter measure?", answer: "It measures total characters, characters without spaces, letter count, and numeric count." },
        { question: "Is this tool free?", answer: "Yes, it's 100% free to use." },
        { question: "How do I check character count?", answer: "Simply paste your text into the text area above, and it will automatically display the count." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-slate-900">{text.length}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Total Characters</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-slate-900">{text.replace(/\s/g, "").length}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Without Spaces</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-slate-900">{text.replace(/[^a-zA-Z]/g, "").length}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Letters Only</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-slate-900">{text.replace(/[^0-9]/g, "").length}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Numbers Only</div>
          </div>
        </div>
        <Textarea value={text} onChange={e => setText(e.target.value)} placeholder="Type or paste your text here to count characters..." className="min-h-[200px]" />
      </div>
    </ToolPageLayout>
  );
}

export function ReadingTimeCalculator() {
  const [text, setText] = useState("");
  const [wpm, setWpm] = useState(200);
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  const minutes = words / wpm;
  return (
    <ToolPageLayout
      title="Reading Time Calculator"
      description="Estimate how long it will take to read your text, article, or document. Adjust words per minute (WPM) for personalized results."
      h1="Reading Time Calculator"
      category="Text & Word"
      categoryPath="/text-word"
      howToUse={[
        "Paste your text, article, or document content.",
        "Optionally adjust the 'Words Per Minute (WPM)' if you read faster or slower than average.",
        "See the estimated reading time instantly."
      ]}
      faq={[
        { question: "What is the average reading speed?", answer: "The average adult reads approximately 200-250 words per minute." },
        { question: "Is this calculator accurate?", answer: "It provides a reliable estimate based on the word count and your chosen WPM setting." },
        { question: "Can I customize the reading speed?", answer: "Yes, you can adjust the Words Per Minute (WPM) setting to match your own reading pace." }
      ]}
    >
      <div className="space-y-6">
        <div className="flex gap-4 items-center">
          <Label>Words Per Minute (WPM):</Label>
          <Input type="number" value={wpm} onChange={e => setWpm(Number(e.target.value) || 200)} className="w-24" />
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 flex flex-col items-center">
          <div className="text-4xl font-bold text-blue-600">{Math.ceil(minutes)} min</div>
          <div className="text-sm text-slate-500 mt-2">({words} words total)</div>
        </div>
        <Textarea value={text} onChange={e => setText(e.target.value)} placeholder="Paste article here..." className="min-h-[250px]" />
      </div>
    </ToolPageLayout>
  );
}

export function WordsToPagesConverter() {
  const [words, setWords] = useState(500);
  const [wordsPerPage, setWordsPerPage] = useState(250); // Standard double spaced
  return (
    <ToolPageLayout
      title="Words to Pages Converter"
      description="Convert your word count to an estimated page count instantly. Easily calculate how many pages your text will fill."
      h1="Words to Pages Converter"
      category="Text & Word"
      categoryPath="/text-word"
      howToUse={[
        "Enter your total word count.",
        "Select your preferred formatting (e.g., double-spaced, single-spaced).",
        "View your estimated page count instantly."
      ]}
      faq={[
        { question: "How many pages is 500 words?", answer: "At 250 words per page (double-spaced), 500 words equal approximately 2 pages." },
        { question: "How many pages is 1000 words?", answer: "At 250 words per page, 1000 words equal approximately 4 pages." },
        { question: "Is 250 words a page?", answer: "Yes, 250 words is the standard for one double-spaced page." }
      ]}
    >
      <div className="space-y-6 max-w-xl mx-auto">
        <div className="space-y-2">
          <Label>Total Words</Label>
          <Input type="number" value={words} onChange={e => setWords(Number(e.target.value))} />
        </div>
        <div className="space-y-2">
          <Label>Words per Page</Label>
          <select 
            className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            value={wordsPerPage}
            onChange={e => setWordsPerPage(Number(e.target.value))}
          >
            <option value={250}>250 (Double Spaced)</option>
            <option value={500}>500 (Single Spaced)</option>
            <option value={300}>300 (Standard Book)</option>
          </select>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center mt-6">
          <div className="text-4xl font-bold text-blue-700">{(words / wordsPerPage).toFixed(1)}</div>
          <div className="text-sm text-blue-600 uppercase tracking-wider mt-2">Estimated Pages</div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function LineBreakRemover() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState<"space" | "paragraph">("space");

  const process = () => {
    if (mode === "space") {
      setOutput(input.replace(/\r?\n|\r/g, " "));
    } else {
      setOutput(input.replace(/(?<!\n)\n(?!\n)/g, " "));
    }
  };

  return (
    <ToolPageLayout
      title="Remove Line Breaks Online"
      description="Quickly remove line breaks and newlines from your text. Choose between removing all breaks or preserving paragraphs."
      h1="Remove Line Breaks Online"
      category="Text & Word"
      categoryPath="/text-word"
      howToUse={[
        "Paste your text with line breaks into the input area.",
        "Select 'Remove All Breaks' or 'Preserve Paragraphs'.",
        "Click 'Process' to get your cleaned text."
      ]}
      faq={[
        { question: "How do I remove line breaks from text?", answer: "Simply paste your text into our tool, select your preferred mode, and click 'Process'." },
        { question: "Can I remove line breaks but keep paragraphs?", answer: "Yes, use the 'Preserve Paragraphs' option to keep double line breaks while removing single ones." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-2">
          <Button onClick={() => setMode("space")} variant={mode === "space" ? "default" : "outline"} className="flex-1 whitespace-normal h-auto py-2">Remove All Breaks</Button>
          <Button onClick={() => setMode("paragraph")} variant={mode === "paragraph" ? "default" : "outline"} className="flex-1 whitespace-normal h-auto py-2">Preserve Paragraphs</Button>
          <Button onClick={process} className="w-full sm:w-auto">Process</Button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Textarea value={input} onChange={e => setInput(e.target.value)} placeholder="Input with breaks..." className="min-h-[300px]" />
          <Textarea value={output} readOnly className="min-h-[300px] bg-slate-50" />
        </div>
      </div>
    </ToolPageLayout>
  );
}
